/**
 * LibrarySetup - Felles komponent for oppsett av lokal fillagring
 * Brukes av alle moduler som trenger File System API
 * @version 1.0.0 - MASTERPROMPT v2.0 compliant (Fase 3 - Deduplisering)
 */

import React from 'react';
import { HardDrive, FolderOpen, CheckCircle, Shield } from 'lucide-react';

export function LibrarySetup({ 
  moduleTitle = 'modulen',
  onSetup,
  features = [],
  description = 'Velg en mappe på din enhet der RettVerk skal lagre dokumentene dine. All data forblir på din maskin – trygt og privat.'
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-20 h-20 bg-[var(--copper)]/10 rounded-2xl flex items-center justify-center mb-6">
        <HardDrive className="w-10 h-10 text-[var(--copper)]" />
      </div>
      
      <h2 className="text-2xl font-bold text-[var(--text-main)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
        Sett opp {moduleTitle}-biblioteket
      </h2>
      
      <p className="text-[var(--text-muted)] text-center max-w-md mb-6">
        {description}
      </p>
      
      <button
        onClick={onSetup}
        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[var(--copper)] to-[var(--copper-dark)] rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
        data-testid="setup-library-btn"
      >
        <FolderOpen className="w-5 h-5" />
        Velg lagringsmappe
      </button>
      
      {/* Features grid */}
      {features.length > 0 && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4 max-w-3xl">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div key={i} className="p-4 bg-[var(--bg-highlight)]/50 border border-[var(--border-subtle)] rounded-lg text-center">
                {Icon && <Icon className="w-6 h-6 text-[var(--copper)] mx-auto mb-2" />}
                <h3 className="text-sm font-medium text-[var(--text-main)]">{feature.title}</h3>
                <p className="text-xs text-[var(--text-muted)]">{feature.description}</p>
              </div>
            );
          })}
        </div>
      )}
      
      {/* What gets stored */}
      <div className="mt-8 p-4 bg-[var(--bg-highlight)]/50 border border-[var(--border-subtle)] rounded-lg max-w-md">
        <h3 className="text-sm font-medium text-[var(--text-main)] mb-2">Hva lagres?</h3>
        <ul className="text-xs text-[var(--text-muted)] space-y-1">
          <li className="flex items-center gap-2">
            <CheckCircle className="w-3 h-3 text-[var(--success)]" />
            Saker og dokumenter
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-3 h-3 text-[var(--success)]" />
            Genererte brev og rapporter
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-3 h-3 text-[var(--success)]" />
            Vedlegg og kvitteringer
          </li>
        </ul>
      </div>
      
      {/* Privacy notice */}
      <div className="mt-4 flex items-center gap-2 text-xs text-[var(--success)]">
        <Shield className="w-4 h-4" />
        <span>Privacy-first: Ingen data sendes til våre servere</span>
      </div>
    </div>
  );
}

export default LibrarySetup;
